import { useQuery } from '@apollo/client';
import {
  AppContent,
  AppPage,
  Breadcrumbs,
  ErrorComponents,
  formatTermPaymentName,
  PageHeaderV2,
  SummarySection,
  useFeatureToggle,
  useTitle,
} from '@css-xp/base';
import { AnalyticsEventSource, ScreenEventAnalytics } from '@atlassian/temp-analytics';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { Maybe, TRANSACTION_ACCOUNT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { ENABLE_RECENTLY_VISITED_HISTORY, removeContentScrollingHack } from '@atlassian/cssxp-utils-generic';
import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import { fetchTermDetails } from '../queries/fetch-term-details';
import { TermDetailsTab } from '../tabs/term-details-tabs';
import TermDetailsSummary from '../term-details-summary/term-details-summary';
import './term-details-page.scss';
import { get } from 'lodash';
import { TermDetailsAccount } from '@css-xp/graphtypes/src/generated-types-new';
import Lozenge from '@atlaskit/lozenge';
import { txaPushPaymentStatus } from '@atlassian/cssxp-types-billing';

interface Props {
  appPageClass?: string;
  title?: string;
}

export const TermDetailsPage: React.FC<Props> = ({ appPageClass, title }) => {
  useEffect(() => {
    removeContentScrollingHack();
  }, []);

  const { id } = useParams<{ id: string }>();
  const [isEnabled] = useFeatureToggle();

  const transactionAccountPageResult = useQuery<
    generatedTypesNew.FetchTermDetailsQuery,
    generatedTypesNew.FetchAddOnsForEntitlementQueryVariables
  >(fetchTermDetails, {
    variables: { id: id },
    fetchPolicy: 'no-cache',
    skip: !id,
  });
  const { data, loading, error } = transactionAccountPageResult;
  if (isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTitle(data?.transactionAccount?.transactionAccountNumber ?? '', title);
  }

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  const transactionAccount = data?.transactionAccount;
  const billToAddress = transactionAccount?.billToParty;
  const termType = transactionAccount?.billingAccountsList?.paymentMethod;
  const currentAccount: TermDetailsAccount = get(
    transactionAccount?.billingAccountsList?.nodes?.filter(
      (node) => node?.transactionAccountNumber === transactionAccount.transactionAccountNumber
    ),
    '0'
  );

  const renderBreadcrumbs = () => {
    if (transactionAccount?.transactionAccountNumber) {
      const breadcrumbTrail = [
        {
          title: billToAddress?.name ?? 'Billing account',
          href: window.location.pathname,
        },
        {
          title: transactionAccount?.transactionAccountNumber,
          href: `${TRANSACTION_ACCOUNT_PATH_2}/${id}`,
        },
        {
          title: 'Account balance',
          href: window.location.pathname,
        },
      ];

      return <Breadcrumbs items={breadcrumbTrail} />;
    }
  };

  const getHeaderLozenges = () => {
    if (currentAccount?.status === txaPushPaymentStatus.INELIGIBLE) {
      return (
        <span className="ineligible-lozenge">
          <Lozenge appearance="removed">DEACTIVATED</Lozenge>
        </span>
      );
    }
  };

  const pageHeader = (paymentMethod: Maybe<string> | undefined) => {
    return (
      <div className="page-header">
        {`${formatTermPaymentName(paymentMethod)} payment terms`}
        <div>{getHeaderLozenges()}</div>
      </div>
    );
  };

  return (
    <AppPage className={`${appPageClass} txa-term-details-page`}>
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.CREDIT_DETAILS,
          attributes: {
            id,
            entityName: title,
          },
        }}
      />
      {renderBreadcrumbs()}
      <AppContent>
        <PageHeaderV2 header={pageHeader(termType)} />
        <SummarySection>
          <TermDetailsSummary
            transactionAccount={transactionAccount as generatedTypesNew.TransactionAccount}
            currentAccount={currentAccount}
          />
        </SummarySection>
        <TermDetailsTab
          isLoading={loading}
          transactionAccount={transactionAccount as generatedTypesNew.TransactionAccount}
        />
      </AppContent>
    </AppPage>
  );
};

export default TermDetailsPage;
